import React, { memo, forwardRef, useContext } from 'react';
import cn from 'classnames';
import { useCalendarCell } from '@react-aria/calendar';
import { mergeProps } from '@react-aria/utils';
import { useHover } from '@react-aria/interactions';
import { isSameMonth } from '@internationalized/date';
import { useForwardedRef } from '../../hooks';
import { CalendarContext } from './contexts';
import { useSlot } from '../slots';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import styles from './styles.module.css';
const ReasonWrapper = memo(function ReasonWrapper({ children, reason, placement = 'top' }) {
    return reason ? (React.createElement(Tooltip, { placement: placement, title: reason, delay: 500 }, children)) : (children);
});
export const CalendarCell = memo(forwardRef(function CalendarCell({ date, currentMonth, ...rest }, ref) {
    const context = useContext(CalendarContext);
    if (!context) {
        throw new Error('CalendarCell should be used inside CalendarBase component');
    }
    const { state, size, minValueReason = '', maxValueReason = '', minDaysReason = '', reasonPlacement = 'top', } = context;
    const cellRef = useForwardedRef(ref);
    const { cellProps, buttonProps, isSelected, isOutsideVisibleRange, isDisabled, isUnavailable, formattedDate, isFocused, isPressed, } = useCalendarCell({ date }, state, cellRef);
    const { widthClass, heightClass, sizeClass } = useSlot({ size });
    const { hoverProps, isHovered } = useHover({});
    let sameMonth = currentMonth ? isSameMonth(currentMonth, date) : true;
    const { minValue, maxValue } = state;
    const reason = minValue && date.compare(minValue) <= 0
        ? minValueReason
        : maxValue && date.compare(maxValue) >= 0
            ? maxValueReason
            : isUnavailable
                ? minDaysReason
                : undefined;
    const highlightedRange = state.highlightedRange;
    const isInRange = highlightedRange
        ? date.compare(highlightedRange.start) >= 0 && date.compare(highlightedRange.end) <= 0
        : false;
    return (React.createElement("td", { ...cellProps, 
        // useCalendarCell делает scrollIntoView, что в куче кейсов может приводить
        // к каким-то диким прыжкам скролла, скролить оно пытается на ближайшем контейнере
        // с подходящим overflow, так что выставляем его прям на ячейке, чтобы вырубить подскрол
        style: { overflow: 'auto' } },
        React.createElement(ReasonWrapper, { reason: reason, placement: reasonPlacement },
            React.createElement(Typography, { ...mergeProps(rest, buttonProps, hoverProps), variant: "body2", Component: "div", color: "main", ref: cellRef, hidden: isOutsideVisibleRange, className: cn(styles.cell, widthClass, heightClass, sizeClass, {
                    [styles.selected]: (isSelected || isInRange) && sameMonth,
                    [styles.unavailable]: isUnavailable || !sameMonth,
                    [styles.disabled]: isDisabled || !sameMonth,
                    [styles.focused]: isFocused && sameMonth,
                    [styles.pressed]: isPressed && sameMonth,
                    [styles.hovered]: isHovered && !isDisabled && sameMonth,
                }) }, formattedDate))));
}));
